// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
  zIndex,
} from 'presentations/views/styles';
import { gray04, white } from 'presentations/views/styles/color';
import { FormPageItem } from 'presentations/api/schema/form';
import { TeamItem } from 'presentations/api/schema/team';
import EventTypeSubmitField from 'presentations/views/components/molecules/EventTypeSubmitField';
import QuestionFormListItem from 'presentations/views/components/molecules/QuestionFormListItem';
import { VisualViewType } from 'domains/models/form/visualViewType';
import Headline from 'presentations/views/components/atoms/Headline';
import Workflow from 'domains/models/workflow';
import EventMainVisual from 'presentations/views/components/organisms/EventMainVisual';
import RichReadView from 'presentations/views/components/atoms/RichReadView';
import { ErrorList } from 'domains/models';
import { ViewerItem } from 'presentations/api/schema/user';

export interface Props {
  viewer: ViewerItem | null;
  form: FormPageItem;
  team: TeamItem;
  isLightColor: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isWidget?: boolean;

  page: number;
  customQuestions: Workflow[];
  email: string | null;
  name: string | null;
  phoneNumber: string | null;
  customQuestionAnswers: any[];
  customOtherAnswers: string[];
  errors: ErrorList;

  onSubmitForm: (e: SyntheticEvent<HTMLFormElement>) => void;
  onSubmit: () => void;
  onChange: (e: any) => void;
  onChangeOtherAnswer: (e: any) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isColored = props.form.visualViewType === VisualViewType.Colored;

  return (
    <PageContainer
      isColored={isColored}
      isWidget={props.isWidget}
      isShowImage={!!props.form.coverImage && props.page === 0}
    >
      <FirstView isColored={isColored}>
        <EventMainVisual coverImage={props.form.coverImage} />
        <FirstViewText isColored={isColored}>
          <TitleContainer>
            <TitleHeadline variant="h1">{props.form.name}</TitleHeadline>
          </TitleContainer>
          {props.form.description && (
            <DescriptionContainer>
              <RichReadView
                className="jicoo-editor-body jicoo-editor-view"
                content={props.form.description}
              />
            </DescriptionContainer>
          )}
          {!isColored && <FirstViewBorder />}
        </FirstViewText>
      </FirstView>
      <MainContainer>
        <MainScreen isColored={isColored}>
          <form onSubmit={props.onSubmitForm}>
            {props.customQuestions.map((question, index) => {
              return (
                <QuestionContainer key={question.uid}>
                  <QuestionPage isColored={isColored}>
                    <QuestionFormListItem
                      viewer={props.viewer}
                      email={props.email}
                      name={props.name}
                      phoneNumber={props.phoneNumber}
                      isColored={isColored}
                      isFullWidth={false}
                      question={question}
                      color={props.form.color}
                      index={index}
                      currentIndex={props.page - 1}
                      customOtherAnswers={props.customOtherAnswers}
                      customQuestionAnswers={props.customQuestionAnswers}
                      onChange={props.onChange}
                      onChangeOtherAnswer={props.onChangeOtherAnswer}
                      errors={props.errors}
                    />
                  </QuestionPage>
                </QuestionContainer>
              );
            })}
            <Submit>
              <EventTypeSubmitField
                isLoading={props.isLoading}
                onSubmit={props.onSubmit}
                submitLabel={t('Complete')}
                isDisabled={props.isDisabled}
                color={props.form.color}
                isLightColor={props.isLightColor}
              />
            </Submit>
          </form>
        </MainScreen>
      </MainContainer>
    </PageContainer>
  );
}

const PageContainer = styled.div<{
  isColored?: boolean;
  isWidget?: boolean;
  isShowImage?: boolean;
}>`
  ${(props) =>
    props.isWidget
      ? css`
          padding: 0;
        `
      : css`
          padding: ${spacing(48)} ${spacing(24)} 0;
        `}

  background-color: ${white};

  ${mediaMax.tablet} {
    width: 100%;
    padding: 0;
  }
`;
const MainScreen = styled.div<{ isColored?: boolean }>`
  ${(props) =>
    props.isColored
      ? css`
          ${mediaMax.tablet} {
            margin: 0 ${spacing(12)};
            border-radius: 0;
            border: 0;
          }
        `
      : css`
          border: 1px solid ${gray04(props)};
          border-top: 0;
          border-radius: 0 0 ${borderRadius(16)} ${borderRadius(16)};
          ${mediaMax.tablet} {
            border-radius: ${borderRadius(16)};
            border: 1px solid ${gray04(props)};
            margin: 0 ${spacing(12)};
            padding: ${spacing(20)} 0;
          }
        `}
`;
const MainContainer = styled.div`
  position: relative;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
`;

const Submit = styled.div`
  position: sticky;
  z-index: ${zIndex(300)};
  bottom: 0;
  width: 100%;
  height: 70px;
  padding-top: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  background-color: ${white};
  ${mediaMax.smartphone} {
    position: inherit;
  }
`;

const QuestionContainer = styled.div`
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
  }
`;

const QuestionPage = styled.div<{ isColored: boolean }>`
  margin: 0 0 ${spacing(40)};
  :last-of-type {
    margin-bottom: 0;
  }
  ${(props) =>
    !props.isColored &&
    css`
      margin: 0 ${spacing(40)} ${spacing(40)};
      ${mediaMax.tablet} {
        margin: 0 ${spacing(20)} ${spacing(40)};
      }
    `}
`;

const FirstView = styled.div<{ isColored?: boolean }>`
  max-width: 960px;
  overflow: hidden;
  margin: 0 auto;
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    border-radius: 0;
    margin-bottom: 0;
    border: 0;
  }
  ${(props) =>
    props.isColored
      ? css``
      : css`
          margin-bottom: 0;
          border-bottom: 0;
          border-radius: ${borderRadius(16)} ${borderRadius(16)} 0 0;
        `}
`;
const EventMainVisualContainer = styled.div`
  flex: 1;
  height: 260px;
  overflow: hidden;
  ${mediaMax.tablet} {
    border-radius: 0;
    overflow: auto;
  }
`;
const FirstViewText = styled.div<{ isColored?: boolean }>`
  flex: 1;
  padding: ${spacing(40)} 0;
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)};
  }
  ${(props) =>
    props.isColored
      ? css``
      : css`
          padding: ${spacing(40)} ${spacing(40)};
          ${mediaMax.tablet} {
            border: 0;
          }
        `}
`;
const FirstViewBorder = styled.div`
  height: 1px;
  border-bottom: 1px solid ${gray04};
  margin-top: ${spacing(40)};
  ${mediaMax.tablet} {
    display: none;
  }
`;
const TitleHeadline = styled(Headline)`
  font-size: ${fontSize(20)};
  line-height: 1.4;
`;
const DescriptionContainer = styled.div<{ isColored?: boolean }>`
  margin: ${spacing(16)} ${spacing(40)} 0;
  ${mediaMax.tablet} {
    margin: ${spacing(16)} ${spacing(16)} 0;
  }
`;

const TitleContainer = styled.div<{ isColored?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 ${spacing(40)};
  justify-content: center;
  ${mediaMax.tablet} {
    margin: 0 ${spacing(16)};
  }
`;
